import {inject, Injectable} from '@angular/core';
import {FloatCellDefinition} from '../../../components/float-layout';
import {Widget} from '../../../components/layout/widget/widget';
import {WidgetDefinition} from '../../../components/layout/widget/widget-definition';
import {WidgetRegistryService} from '../../../components/layout/widget/widget-registry.service';
import {WidgetSettingsDefinition} from '../../../components/layout/widget/widget-settings-definition';
import {UserService} from '../../../services/user.service';
import {FloatLayoutService} from './float-layout.service';

interface GenericWidgetDefinition<S_DEF extends WidgetSettingsDefinition> extends WidgetDefinition {
    settings: S_DEF;
}

@Injectable()
export class FloatWidgetFactory {

    private readonly widget? = inject(Widget, {optional: true});
    private readonly floatLayoutService = inject(FloatLayoutService);
    private readonly userService = inject(UserService);
    private readonly widgetRegistryService = inject(WidgetRegistryService);

    public createFloatWidget<S_DEF extends WidgetSettingsDefinition>(
        widgetTypeId: string,
        settings?: Omit<S_DEF, 'title'> & Partial<S_DEF>
    ): void {
        const isAllowed = this.isAllowedForCurrentUser(widgetTypeId);

        if (isAllowed) {
            const tabId = this.widget?.tabId || this.floatLayoutService.floatLayout.tabId;
            const parentCell = this.widget?.cell;

            const floatCellDef: FloatCellDefinition = {
                id:                null,
                pos:               [null, null],
                size:              [null, null],
                activeWidgetIndex: 0,
                widgets:           []
            };
            const widgetDef: GenericWidgetDefinition<S_DEF> = {
                id:           null,
                tabId,
                cellId:       null,
                widgetTypeId,
                settings: {
                    title: null,
                    ...settings
                } as S_DEF
            };

            this.floatLayoutService.createFloatCellByDefinition(floatCellDef, widgetDef, parentCell);
        }
    }

    public isAllowedForCurrentUser(widgetTypeId: string): boolean {
        const user = this.userService.user;
        const widgetDescription = this.widgetRegistryService.getWidgetDescription(widgetTypeId);

        return !widgetDescription.userRoles
            || widgetDescription.userRoles.includes(user.role);
    }

}
